import { useEffect, useRef, useState, type ReactNode } from 'react'
import { FileUp, Replace } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { fileToBase64, renderPreviewServer } from '../../../components/createDesignsWizard.logic'
import { useAuthStore } from '../../../stores/authStore'
import { useBackgroundJobsStore } from '../../../stores/backgroundJobsStore'
import { showJobStartedToast, useToastStore } from '../../../stores/toastStore'
import {
  designsV2API,
  type ReplaceDesignSourceMode,
  type ReplaceDesignSourceStart,
} from '../api'
import { SafeImg } from './design-card/parts'
import { ReplaceScenarioFields, hasPublishedCardStatus } from './design-card/ReplaceScenarioFields'

const MAX_PDF_BYTES = 50 * 1024 * 1024

interface EditDesignSourceModalProps {
  isOpen: boolean
  designCode: number | string
  currentPreviewUrl?: string | null
  cardStatuses?: string[]
  onClose: () => void
  onStarted: (result: ReplaceDesignSourceStart) => void
}

export function EditDesignSourceModal({
  isOpen,
  designCode,
  currentPreviewUrl,
  cardStatuses = [],
  onClose,
  onStarted,
}: EditDesignSourceModalProps) {
  const user = useAuthStore((state) => state.user)
  const addJob = useBackgroundJobsStore((state) => state.addJob)
  const showToast = useToastStore((state) => state.showToast)
  const inputRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<1 | 2>(1)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [mode, setMode] = useState<ReplaceDesignSourceMode>('designs_only')
  /** «Отправить новые фото в опубликованные карточки» (C2, SPEC_C_auto_resend.md) — включено по умолчанию. */
  const [resendPhotos, setResendPhotos] = useState(true)
  const [dragOver, setDragOver] = useState(false)
  const [rendering, setRendering] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState('')

  const hasPublishedCards = hasPublishedCardStatus(cardStatuses)

  useEffect(() => {
    if (isOpen) return
    setStep(1)
    setFile(null)
    setPreview(null)
    setMode('designs_only')
    setResendPhotos(true)
    setDragOver(false)
    setRendering(false)
    setSubmitting(false)
    setConfirmed(false)
    setError('')
  }, [isOpen])

  const selectFiles = async (files: FileList | File[]) => {
    const selected = Array.from(files)
    if (selected.length !== 1) {
      setError('Загрузите ровно один PDF-файл')
      return
    }
    const next = selected[0]
    if (!/\.pdf$/i.test(next.name) || (next.type && next.type !== 'application/pdf')) {
      setError('Выберите PDF-файл с MIME-типом application/pdf')
      return
    }
    if (next.size > MAX_PDF_BYTES) {
      setError('PDF превышает лимит 50 МБ')
      return
    }
    setFile(next)
    setError('')
    setPreview(null)
    setRendering(true)
    const rendered = await renderPreviewServer(next)
    setRendering(false)
    setPreview(rendered)
    if (!rendered) setError('Сервер не смог построить превью PDF')
  }

  const submit = async () => {
    if (!file || !preview || !confirmed) return
    setSubmitting(true)
    setError('')
    try {
      const pdfFile = await fileToBase64(file)
      const result = await designsV2API.replaceDesignSource(designCode, {
        pdfFile,
        pdfFileName: file.name,
        pdfMimeType: file.type || 'application/pdf',
        mode,
        resend_photos: resendPhotos,
      })
      if (user?.user_id) {
        addJob({
          jobId: result.jobId,
          userId: user.user_id,
          type: 'design_source_replacement',
          status: 'processing',
          name: `Замена макета #${designCode}`,
          currentStep: 'Замена PDF',
          progress: { current: 0, total: mode === 'designs_only' ? 2 : 3, percentage: 0 },
          steps: [],
          startedAt: new Date(),
          metadata: { design_code: String(designCode), mode, skipped: result.skipped },
        })
      }
      showJobStartedToast(useToastStore.getState(), result.jobId, `Замена макета #${designCode}`)
      if (result.warnings?.length) {
        showToast(result.warnings.join(' '), 'warning')
      }
      onStarted(result)
      onClose()
    } catch (caught: any) {
      setError(
        caught?.response?.data?.error?.message
          || caught?.message
          || 'Не удалось запустить замену макета',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Изменить макет"
      subtitle={`Макет #${designCode} · шаг ${step} из 2`}
      size="xl"
      layer="nested"
      closeOnOverlayClick={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={step === 1 ? onClose : () => setStep(1)} disabled={submitting}>
            {step === 1 ? 'Отмена' : 'Назад'}
          </Button>
          {step === 1 ? (
            <Button
              variant="primary"
              onClick={() => setStep(2)}
              disabled={!file || !preview || rendering}
            >
              Далее
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={<Replace className="h-4 w-4" />}
              loading={submitting}
              disabled={!confirmed}
              onClick={() => { void submit() }}
            >
              Заменить макет
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-5">
        {step === 1 ? (
          <>
            <input
              ref={inputRef}
              type="file"
              aria-label="Новый PDF макета"
              accept="application/pdf,.pdf"
              multiple={false}
              className="hidden"
              onChange={(event) => {
                if (event.target.files) void selectFiles(event.target.files)
                event.target.value = ''
              }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragEnter={(event) => { event.preventDefault(); setDragOver(true) }}
              onDragOver={(event) => { event.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => {
                event.preventDefault()
                setDragOver(false)
                void selectFiles(event.dataTransfer.files)
              }}
              className={`flex min-h-32 w-full flex-col items-center justify-center rounded-xl border-2 border-dashed p-5 text-center transition-colors ${
                dragOver
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                  : 'border-gray-300 bg-gray-50 hover:border-indigo-400 dark:border-zinc-700 dark:bg-zinc-800/50'
              }`}
            >
              <FileUp className="mb-2 h-7 w-7 text-indigo-500" />
              <span className="text-sm font-semibold">Перетащите один PDF или выберите файл</span>
              <span className="mt-1 text-xs text-gray-500 dark:text-zinc-400">До 50 МБ</span>
            </button>
            {file && <p className="text-sm text-gray-600 dark:text-zinc-300">{file.name}</p>}

            <div className="grid gap-4 sm:grid-cols-2">
              <PreviewCard title="Было">
                {currentPreviewUrl ? (
                  <SafeImg src={currentPreviewUrl} alt={`Текущий макет ${designCode}`} className="h-full w-full object-contain" />
                ) : (
                  <span className="text-xs text-gray-400">Текущее превью отсутствует</span>
                )}
              </PreviewCard>
              <PreviewCard title="Стало">
                {rendering ? (
                  <span className="animate-pulse text-xs text-gray-400">Сервер строит превью…</span>
                ) : preview ? (
                  <img src={preview} alt={`Новое превью ${designCode}`} className="h-full w-full object-contain" />
                ) : (
                  <span className="text-xs text-gray-400">Выберите PDF</span>
                )}
              </PreviewCard>
            </div>
          </>
        ) : (
          <ReplaceScenarioFields
            mode={mode}
            onModeChange={(value) => { setMode(value); setConfirmed(false) }}
            resendPhotos={resendPhotos}
            onResendPhotosChange={setResendPhotos}
            confirmed={confirmed}
            onConfirmedChange={setConfirmed}
            hasPublishedCards={hasPublishedCards}
            confirmText="Подтверждаю замену исходного PDF и запуск выбранного сценария. Старый PDF будет перезаписан."
          />
        )}

        {error && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}
      </div>
    </Modal>
  )
}

function PreviewCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-gray-700 dark:text-zinc-200">{title}</p>
      <div className="flex aspect-[210/297] max-h-80 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white p-2 dark:border-zinc-700">
        {children}
      </div>
    </div>
  )
}

export default EditDesignSourceModal
